import React, { useEffect } from 'react';
import './About.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const About = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  return (
    <div className="about-master">
      <section className="hero-viewport">
        <div className="hero-overlay"></div>
        <div className="hero-content" data-aos="zoom-out">
          <h1 className="display-title">About Us</h1>
        </div>
      </section>

      <div className="content-wrap">
        <section className="mission-grid">
          <div className="text-box" data-aos="fade-right">
            <h2 className="section-label">Our Origin</h2>
            <p className="lead-text">
              Founded in 2025 by <strong>Amit Choudhary</strong>, Garshya was created with a clear intention:
              to build a marketplace that respects your time, your trust, and your standards.
            </p>
            <p className="sub-text">
              In today's fast-moving digital world, shopping often feels overwhelming — endless options,
              inconsistent quality, and uncertainty at every step. We are here to bring back clarity,
              confidence, and control.
            </p>
          </div>
          <div className="image-box" data-aos="fade-left">
            <div className="glass-inner-card">
              <i className="bi bi-quote-left"></i>
              <p>We are not here to offer everything. We are here to offer what truly deserves your attention.</p>
            </div>
          </div>
        </section>

        <section className="story-focus" data-aos="fade-up">
          <div className="glass-center">
            <h3>The Garshya Story</h3>
            <p>
              Garshya began with a simple but powerful thought: When everything is available, what actually matters?
              The answer was never "more." It was always <strong>better</strong>.
            </p>
            <p>
              Instead of building a platform driven by volume, Garshya was designed as a curated experience —
              a place where every product and service is carefully selected, reviewed, and aligned with a
              defined standard of quality.
            </p>
            <p>
              Every listing you see has passed through intention. Every choice you make is backed by trust.
              Because we believe shopping should feel effortless, not exhausting. It should feel refined,
              not random. It should feel right — every single time.
            </p>
            <p><strong>At Garshya, quality is not a feature. It is the foundation.</strong></p>
            <div className="divider-glow"></div>
          </div>
        </section>

        {/* ── Why Garshya — enhanced with trust & conversion pillars ── */}
        <section className="pillars-section">
          <h2 className="text-center mb-5" data-aos="fade-down">Why Garshya</h2>
          <div className="pillars-grid">
            {/* Core trust pillars */}
            <div className="pillar-card pillar-highlight" data-aos="flip-left" data-aos-delay="100">
              <i className="bi bi-funnel-fill"></i>
              <h4>Curated Products</h4>
              <p>Every product on Garshya is hand-picked. We skip the noise so you never have to wade through it.</p>
            </div>
            <div className="pillar-card pillar-highlight" data-aos="flip-left" data-aos-delay="200">
              <i className="bi bi-award-fill"></i>
              <h4>Quality First</h4>
              <p>We set the bar high and keep it there. No product reaches you without meeting our quality threshold.</p>
            </div>
            <div className="pillar-card pillar-highlight" data-aos="flip-left" data-aos-delay="300">
              <i className="bi bi-patch-check-fill"></i>
              <h4>Trusted Sellers</h4>
              <p>Our seller network is vetted and held to strict standards — your trust is never taken for granted.</p>
            </div>

            {/* Original pillars */}
            <div className="pillar-card" data-aos="flip-left" data-aos-delay="400">
              <i className="bi bi-stars"></i>
              <h4>Curated Selection</h4>
              <p>We focus on thoughtful choices, not endless listings.</p>
            </div>
            <div className="pillar-card" data-aos="flip-left" data-aos-delay="500">
              <i className="bi bi-shield-check"></i>
              <h4>Uncompromising Quality</h4>
              <p>Every product and service is evaluated against strict standards before it reaches you.</p>
            </div>
            <div className="pillar-card" data-aos="flip-left" data-aos-delay="600">
              <i className="bi bi-people"></i>
              <h4>Trusted Ecosystem</h4>
              <p>We work with carefully selected partners who share our commitment to reliability and excellence.</p>
            </div>
            <div className="pillar-card" data-aos="flip-left" data-aos-delay="700">
              <i className="bi bi-eye"></i>
              <h4>Transparent Practices</h4>
              <p>We aim to provide clear, accurate, and honest information to help you make informed decisions.</p>
            </div>
            <div className="pillar-card" data-aos="flip-left" data-aos-delay="800">
              <i className="bi bi-gem"></i>
              <h4>Refined Experience</h4>
              <p>Designed to be seamless, intuitive, and premium at every touchpoint.</p>
            </div>
            <div className="pillar-card" data-aos="flip-left" data-aos-delay="900">
                <i className="bi bi-truck"></i>
                <h4>Reliable Delivery</h4>
                <p>From checkout to doorstep, we ensure fast, secure, and dependable delivery for every order.</p>
              </div>

              <div className="pillar-card" data-aos="flip-left" data-aos-delay="1000">
                <i className="bi bi-headset"></i>
                <h4>Customer Support</h4>
                <p>Our support team is always ready to assist you with quick responses and customer-first solutions.</p>
              </div>
          </div>
        </section>

        {/* ── Micro Trust Signals Block ── */}
        <section className="trust-signals-section" data-aos="fade-up">
          <h2 className="text-center mb-4" data-aos="fade-down">Built on Trust</h2>
          <div className="trust-signals-grid">
            <div className="trust-signal-card" data-aos="zoom-in" data-aos-delay="100">
              <div className="trust-signal-icon">
                <i className="bi bi-lock-fill"></i>
              </div>
              <h5>Secure Payments</h5>
              <p>Every transaction is protected with industry-standard encryption. Shop with complete peace of mind.</p>
            </div>
            <div className="trust-signal-card" data-aos="zoom-in" data-aos-delay="200">
              <div className="trust-signal-icon">
                <i className="bi bi-shield-fill-check"></i>
              </div>
              <h5>No Compromise on Quality</h5>
              <p>We reject the ordinary. Substandard products simply don't make it onto our platform — period.</p>
            </div>
            <div className="trust-signal-card" data-aos="zoom-in" data-aos-delay="300">
              <div className="trust-signal-icon">
                <i className="bi bi-shop-window"></i>
              </div>
              <h5>Verified Sellers</h5>
              <p>Every seller undergoes a thorough vetting process before they can list on Garshya.</p>
            </div>
            <div className="trust-signal-card" data-aos="zoom-in" data-aos-delay="400">
              <div className="trust-signal-icon">
                <i className="bi bi-arrow-counterclockwise"></i>
              </div>
              <h5>Hassle-Free Returns</h5>
              <p>If it's not right, we make it right. Simple, transparent, no fine print.</p>
            </div>
            
          </div>
        </section>

        <section className="vision-banner" data-aos="zoom-in">
          <div className="vision-glass">
            <h3>Our Vision</h3>
            <p>
              To build a marketplace where trust replaces doubt, where quality defines every decision,
              and where every interaction reflects care, credibility, and consistency.
            </p>
            <p>
              Garshya is committed to operating with integrity, fairness, and customer-first principles,
              in alignment with applicable laws and responsible business practices in India.
            </p>
            <p><em>Garshya isn't about more. It's about making every choice count.</em></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;